"use client";

import { Command } from "cmdk";
import { useRouter } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { WRENCH_ROUTES, type WrenchRoute } from "./routes";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const GROUP_LABELS: Record<WrenchRoute["group"], string> = {
  creator: "Creator tools",
  support: "Guides & support",
  account: "Account",
  info: "Legal & info",
};

export function WrenchCommandPalette({ open, onOpenChange }: Props) {
  const router = useRouter();
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (open) setQuery("");
  }, [open]);

  const grouped = useMemo(() => {
    const groups: Record<WrenchRoute["group"], WrenchRoute[]> = {
      creator: [],
      support: [],
      account: [],
      info: [],
    };
    for (const route of WRENCH_ROUTES) groups[route.group].push(route);
    return groups;
  }, []);

  const navigate = (path: string) => {
    onOpenChange(false);
    router.push(path);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center bg-background/70 px-4 pt-[18vh] backdrop-blur-md"
      onClick={() => onOpenChange(false)}
    >
      <div
        className="premium-panel w-full max-w-2xl overflow-hidden rounded-[1.5rem]"
        onClick={(e) => e.stopPropagation()}
      >
        <Command label="Wrench command palette" loop className="flex h-full flex-col">
          <div className="flex items-center gap-3 border-b border-border/60 px-5 py-4">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Command.Input
              autoFocus
              value={query}
              onValueChange={setQuery}
              placeholder="Search pages, tools, features..."
              className="flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground/70"
            />
            <span className="hidden items-center gap-1.5 sm:flex">
              <Kbd>Esc</Kbd>
            </span>
          </div>

          <Command.List className="scrollbar-thin max-h-[55vh] overflow-y-auto px-2 py-2">
            <Command.Empty className="py-12 text-center text-xs text-muted-foreground">
              No pages match — try a different search.
            </Command.Empty>
            {(Object.keys(grouped) as WrenchRoute["group"][]).map((groupKey) => {
              const items = grouped[groupKey];
              if (items.length === 0) return null;
              return (
                <Command.Group
                  key={groupKey}
                  heading={GROUP_LABELS[groupKey]}
                  className="mb-2 [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:font-mono [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[0.22em] [&_[cmdk-group-heading]]:text-muted-foreground"
                >
                  {items.map((route) => (
                    <Command.Item
                      key={route.path}
                      value={`${route.title} ${route.keywords} ${route.path}`}
                      onSelect={() => navigate(route.path)}
                      className="flex cursor-pointer items-start gap-3 rounded-xl px-3 py-2.5 text-sm text-foreground/90 transition aria-selected:bg-accent/10 aria-selected:text-foreground aria-selected:[box-shadow:inset_0_0_0_1px_hsl(var(--accent)/0.35)]"
                    >
                      <span className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                        {route.path}
                      </span>
                      <span className="flex-1">
                        <span className="block text-sm font-medium tracking-tight text-foreground">{route.title}</span>
                        <span className="mt-0.5 block text-[11px] leading-snug text-muted-foreground">
                          {route.description}
                        </span>
                      </span>
                    </Command.Item>
                  ))}
                </Command.Group>
              );
            })}
          </Command.List>

          <div className="flex flex-wrap items-center gap-3 border-t border-border/60 bg-secondary/40 px-5 py-2.5 text-[10px] text-muted-foreground">
            <Hint label="navigate">↑↓</Hint>
            <Hint label="open">Enter</Hint>
            <span className="ml-auto font-mono uppercase tracking-[0.22em]">workshop</span>
          </div>
        </Command>
      </div>
    </div>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="inline-flex items-center rounded-md border border-border/70 bg-secondary/70 px-1.5 py-0.5 font-mono text-[10px] text-foreground/80">
      {children}
    </kbd>
  );
}

function Hint({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <Kbd>{children}</Kbd>
      <span>{label}</span>
    </span>
  );
}
